import { TableBody } from '@/components/ui/table'
import { IntuiTableRow } from './IntuiTableRow'
import { IntuiTableEmpty } from './IntuiTableEmpty'
import type { IntuiColumnDef, RowAction } from './types'
import type { ReactNode } from 'react'

interface IntuiTableBodyProps<T> {
  data: T[]
  columns: IntuiColumnDef<T>[]
  getRowId: (row: T) => string
  loading?: boolean
  emptyState?: ReactNode
  compact?: boolean
  striped?: boolean
  bordered?: boolean
  // Selection
  selectable?: boolean
  selectionMode?: 'single' | 'multi'
  selectedIds: Set<string>
  isRowSelectable?: (row: T) => boolean
  onSelectRow: (rowId: string) => void
  // Expand
  expandable?: boolean
  expandedIds: Set<string>
  onToggleExpand: (rowId: string) => void
  getExpandedContent?: (row: T) => ReactNode
  // Interaction
  onRowClick?: (row: T) => void
  onRowDoubleClick?: (row: T) => void
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void
  isRowDisabled?: (row: T) => boolean
  getRowClassName?: (row: T) => string
  // Actions
  rowActions?: RowAction<T>[]
  // Layout
  totalColumnCount: number
}

export function IntuiTableBody<T>({
  data,
  columns,
  getRowId,
  loading,
  emptyState,
  compact,
  striped,
  bordered,
  selectable,
  selectionMode,
  selectedIds,
  isRowSelectable,
  onSelectRow,
  expandable,
  expandedIds,
  onToggleExpand,
  getExpandedContent,
  onRowClick,
  onRowDoubleClick,
  onRowContextMenu,
  isRowDisabled,
  getRowClassName,
  rowActions,
  totalColumnCount,
}: IntuiTableBodyProps<T>) {
  const isEmpty = !loading && data.length === 0

  return (
    <TableBody>
      {loading || isEmpty ? (
        <IntuiTableEmpty
          loading={loading}
          emptyState={emptyState}
          colSpan={totalColumnCount}
          compact={compact}
        />
      ) : (
        data.map((row, index) => {
          const rowId = getRowId(row)
          const disabled = isRowDisabled ? isRowDisabled(row) : false
          const selectable_ = isRowSelectable ? isRowSelectable(row) : true

          return (
            <IntuiTableRow
              key={rowId}
              row={row}
              rowIndex={index}
              rowId={rowId}
              columns={columns}
              compact={compact}
              striped={striped}
              bordered={bordered}
              selectable={selectable}
              selectionMode={selectionMode}
              isSelected={selectedIds.has(rowId)}
              isRowSelectable={selectable_}
              onSelectRow={onSelectRow}
              expandable={expandable}
              isExpanded={expandedIds.has(rowId)}
              onToggleExpand={onToggleExpand}
              getExpandedContent={getExpandedContent}
              onRowClick={onRowClick}
              onRowDoubleClick={onRowDoubleClick}
              onRowContextMenu={onRowContextMenu}
              isDisabled={disabled}
              rowClassName={getRowClassName?.(row)}
              rowActions={rowActions}
              totalColumnCount={totalColumnCount}
            />
          )
        })
      )}
    </TableBody>
  )
}
